import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowUpRight, BarChart3, Cookie, Images, Inbox, Loader2, Lock, LogOut, Mail, MessageSquareQuote, PartyPopper, Pencil, Plus, ShoppingBag,
  ShoppingBasket, Star, Trash2, UtensilsCrossed, X,
} from "lucide-react";
import { api, formatApiError, imgSrc } from "../lib/api";
import { fmt } from "../data/site";
import { PhotoUploader } from "../components/PhotoUploader";
import { CateringEditor } from "../components/admin/CateringEditor";
import { BasketsPanel } from "../components/admin/BasketsPanel";
import { GoogleReviewSetting, SubscribersPanel } from "../components/admin/MarketingPanels";
import { TrafficPanel } from "../components/admin/TrafficPanel";
import { SitePhotosEditor } from "../components/admin/SitePhotosEditor";
import { ExtrasEditor } from "../components/admin/ExtrasEditor";
import { TestimonialsEditor } from "../components/admin/TestimonialsEditor";

const ACTIVE_STATUSES = ["Pending", "Prepared", "Out for Delivery"];
const ORDER_FILTERS = [
  { id: "active", label: "Active", hint: "Paid orders you still need to cook or deliver.", test: (o) => o.payment_status === "paid" && ACTIVE_STATUSES.includes(o.status) },
  { id: "past", label: "Past", hint: "Delivered or cancelled paid orders.", test: (o) => o.payment_status === "paid" && !ACTIVE_STATUSES.includes(o.status) },
  { id: "unpaid", label: "Unpaid / abandoned", hint: "Reached checkout but never paid — worth a follow-up.", test: (o) => o.payment_status !== "paid" },
  { id: "all", label: "All", hint: "Every order ever placed.", test: () => true },
];
const ORDER_STATUSES = ["Pending", "Prepared", "Out for Delivery", "Delivered", "Cancelled"];
const INQUIRY_STATUSES = ["New", "Contacted", "Quoted", "Booked", "Closed"];
const TAG_OPTIONS = ["Chef Favorite", "High Protein", "Comfort", "Plant-Forward", "Low Carb", "Spicy"];

/** Private area: no password form — a one-time sign-in link is emailed to the owner only. */
const RequestLink = () => {
  const [state, setState] = useState("idle");
  const [hint, setHint] = useState("");

  const request = async () => {
    setState("sending");
    try {
      const { data } = await api.post("/auth/request-link");
      setHint(data.email_hint);
      setState("sent");
    } catch (err) {
      toast.error(formatApiError(err));
      setState("idle");
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 pt-32 pb-20" data-testid="admin-private">
      <div className="w-full max-w-md rounded-3xl border border-inkline bg-cream p-8 text-center sm:p-10">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-ink"><Lock size={22} className="text-saffron" /></span>
        <h1 className="mt-6 font-display text-3xl font-semibold text-ink">Private area</h1>
        {state === "sent" ? (
          <p className="mt-3 text-sm leading-relaxed text-muted2" data-testid="admin-link-sent">
            Your private sign-in link was emailed to <strong className="text-ink">{hint}</strong>. It never expires — keep that email or bookmark the link. Opening it keeps you signed in on that device for a week. Check spam if it isn't there within a minute.
          </p>
        ) : (
          <p className="mt-3 text-sm leading-relaxed text-muted2">This dashboard is for the HiPlate kitchen. The private sign-in link is only ever sent to the owner's inbox.</p>
        )}
        <button onClick={request} disabled={state === "sending"} data-testid="admin-request-link-btn"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-spice disabled:opacity-60">
          {state === "sending" && <Loader2 size={15} className="animate-spin" />} {state === "sent" ? "Send another link" : "Email me my sign-in link"}
        </button>
      </div>
    </div>
  );
};


const StatusSelect = ({ value, options, onChange, testid }) => (
  <select
    value={value}
    data-testid={testid}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-lg border border-inkline bg-cream px-2.5 py-1.5 font-mono text-[11px] font-semibold text-ink outline-none focus:border-spice"
  >
    {options.map((o) => <option key={o}>{o}</option>)}
  </select>
);

const EMPTY_ITEM = {
  name: "", desc: "", price: 14, img: "", tags: [], cal: 550, protein: 35,
  chef: "", ingredients: "", active: true,
};

const MenuEditor = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/menu");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });
  const toggleTag = (tag) =>
    setEditing((cur) => ({
      ...cur,
      tags: cur.tags.includes(tag) ? cur.tags.filter((t) => t !== tag) : [...cur.tags, tag],
    }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...editing,
      price: Number(editing.price),
      cal: Number(editing.cal),
      protein: Number(editing.protein),
    };
    try {
      if (editing.id) await api.patch(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success(editing.id ? "Dish updated." : "Dish added — it's live on the menu.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/menu/${item.id}`);
      toast.success(`"${item.name}" removed from the menu.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (item) => {
    try {
      const { id, ...rest } = item;
      await api.patch(`/admin/menu/${item.id}`, { ...rest, active: !item.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-2.5 text-sm text-ink outline-none transition-colors placeholder:text-muted2/60 focus:border-spice";

  return (
    <div data-testid="menu-editor">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted2">
          {items.filter((i) => i.active).length} live dishes · changes appear on the Meal Prep page instantly.
        </p>
        <button
          data-testid="menu-add-btn"
          onClick={() => setEditing({ ...EMPTY_ITEM })}
          className="inline-flex items-center gap-2 rounded-full bg-spice px-5 py-2.5 text-sm font-bold text-parchment transition-colors hover:bg-ink"
        >
          <Plus size={15} /> Add Dish
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`menu-item-${item.id.slice(0, 8)}`}
            className={`rounded-2xl border bg-parchment p-4 transition-all ${item.active ? "border-inkline" : "border-inkline opacity-55"}`}
          >
            <div className="flex gap-4">
              <img src={imgSrc(item.img)} alt={item.name} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-display text-base font-semibold leading-snug text-ink">{item.name}</h4>
                  <span className="shrink-0 font-mono text-sm font-bold text-spice">{fmt(item.price)}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(item.tags || []).map((t) => (
                    <span key={t} className="rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted2">{t}</span>
                  ))}
                  {!item.active && (
                    <span className="rounded-full bg-ink/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink">Hidden</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid={`menu-active-${item.id.slice(0, 8)}`}
                onClick={() => toggleActive(item)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                  item.active ? "bg-halal/10 text-halal" : "bg-ink/10 text-ink"
                }`}
              >
                {item.active ? "Live" : "Hidden"}
              </button>
              <button
                data-testid={`menu-edit-${item.id.slice(0, 8)}`}
                onClick={() => setEditing({ ...item })}
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-parchment transition-colors hover:bg-spice"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`menu-delete-${item.id.slice(0, 8)}`}
                onClick={() => remove(item)}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-spice/40 px-3.5 py-1.5 text-xs font-bold text-spice transition-colors hover:bg-spice hover:text-parchment"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setEditing(null)}
            data-testid="menu-form-modal"
          >
            <motion.form
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={save}
              className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-parchment p-7 sm:rounded-3xl sm:p-9"
              data-testid="menu-form"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl font-semibold text-ink">
                  {editing.id ? "Edit dish" : "New dish"}
                </h3>
                <button type="button" data-testid="menu-cancel-btn" onClick={() => setEditing(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-cream text-ink transition-colors hover:bg-spice hover:text-parchment" aria-label="Close editor">
                  <X size={17} />
                </button>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <input required data-testid="menu-form-name" placeholder="Dish name" value={editing.name} onChange={set("name")} className={inputCls} />
                <input required type="number" step="0.5" min="1" data-testid="menu-form-price" placeholder="Price ($)" value={editing.price} onChange={set("price")} className={inputCls} />
              </div>
              <textarea required data-testid="menu-form-desc" rows={2} placeholder="Short menu description" value={editing.desc} onChange={set("desc")} className={`mt-4 ${inputCls}`} />
              <div className="mt-4">
                <PhotoUploader
                  value={editing.img}
                  onChange={(img) => setEditing((cur) => ({ ...cur, img }))}
                  folder="dishes"
                  label="Dish photo"
                  testid="menu-form-img"
                />
              </div>
              <div className="mt-4">
                <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">Tags (power the shop filters)</div>
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      data-testid={`menu-tag-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => toggleTag(tag)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                        editing.tags.includes(tag) ? "bg-ink text-parchment" : "bg-cream text-ink/70 hover:bg-ink/10"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <input type="number" min="0" data-testid="menu-form-cal" placeholder="Calories" value={editing.cal} onChange={set("cal")} className={inputCls} />
                <input type="number" min="0" data-testid="menu-form-protein" placeholder="Protein (g)" value={editing.protein} onChange={set("protein")} className={inputCls} />
              </div>
              <input data-testid="menu-form-ingredients" placeholder="Ingredients (comma separated)" value={editing.ingredients} onChange={set("ingredients")} className={`mt-4 ${inputCls}`} />
              <textarea data-testid="menu-form-chef" rows={2} placeholder="Chef's note (shown in the dish quick view)" value={editing.chef} onChange={set("chef")} className={`mt-4 ${inputCls}`} />
              <label className="mt-4 flex items-center gap-3 text-sm font-medium text-ink">
                <input
                  type="checkbox"
                  data-testid="menu-form-active"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="h-4 w-4 accent-[#305830]"
                />
                Live on this week's menu
              </label>
              <button
                type="submit"
                data-testid="menu-save-btn"
                disabled={busy}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-spice py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60"
              >
                {busy && <Loader2 size={15} className="animate-spin" />}
                {editing.id ? "Save Changes" : "Add to Menu"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("orders");
  const [orderFilter, setOrderFilter] = useState("active");
  const tabsRef = useRef(null);

  const goTo = (nextTab, filter) => {
    setTab(nextTab);
    if (filter) setOrderFilter(filter);
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, o, i, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/inquiries"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setInquiries(i.data);
      setContacts(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const sendDigest = async () => {
    try {
      await api.post("/admin/digest/send");
      toast.success("Daily digest sent to eathiplate@gmail.com.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  const setOrderStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status, ...(status === "Delivered" && !o.delivered_at ? { delivered_at: new Date().toISOString() } : {}) } : o)));
      toast.success(status === "Delivered" ? "Marked delivered — review request goes out in 24h." : "Order status updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setInquiryStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((is) => is.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Inquiry status updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendReviewRequest = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/review-request`);
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, review_request_sent: true } : o)));
      toast.success("Review request email sent.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === undefined) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center bg-parchment pt-32">
        <Loader2 size={28} className="animate-spin text-spice" data-testid="admin-loading" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="bg-parchment">
        <RequestLink />
      </main>
    );
  }

  const visibleOrders = orders.filter(ORDER_FILTERS.find((f) => f.id === orderFilter).test);

  const tabs = [
    { id: "orders", label: `Meal Orders (${orders.length})`, icon: ShoppingBag, testid: "admin-tab-orders" },
    { id: "baskets", label: "Live Baskets", icon: ShoppingBasket, testid: "admin-tab-baskets" },
    { id: "traffic", label: "Traffic", icon: BarChart3, testid: "admin-tab-traffic" },
    { id: "inquiries", label: `Catering (${inquiries.length})`, icon: UtensilsCrossed, testid: "admin-tab-inquiries" },
    { id: "contacts", label: `Messages (${contacts.length})`, icon: Inbox, testid: "admin-tab-contacts" },
    { id: "menu", label: "Weekly Menu", icon: UtensilsCrossed, testid: "admin-tab-menu" },
    { id: "extras", label: "Sides & Desserts", icon: Cookie, testid: "admin-tab-extras" },
    { id: "catering-menu", label: "Catering Menu", icon: PartyPopper, testid: "admin-tab-catering-menu" },
    { id: "testimonials", label: "Reviews", icon: MessageSquareQuote, testid: "admin-tab-testimonials" },
    { id: "subscribers", label: "Subscribers", icon: Mail, testid: "admin-tab-subscribers" },
    { id: "site-photos", label: "Site Photos", icon: Images, testid: "admin-tab-site-photos" },
  ];

  return (
    <main className="min-h-screen bg-parchment px-4 pb-24 pt-36 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink sm:text-4xl">Kitchen dashboard</h1>
            <p className="mt-1 text-sm text-muted2">Signed in as {user.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-parchment"
            >
              <LogOut size={15} /> Sign Out
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
            {[
              { label: "Active orders", value: stats.active_orders, icon: ShoppingBag, go: () => goTo("orders", "active"), hint: "Paid orders to cook & deliver" },
              { label: "Revenue", value: fmt(stats.revenue), icon: UtensilsCrossed, go: () => goTo("orders", "past"), hint: "All paid orders" },
              { label: "Visitors · 7 days", value: stats.visitors_7d, icon: BarChart3, go: () => goTo("traffic"), hint: "Website performance" },
              { label: "Catering inquiries", value: stats.inquiries, icon: PartyPopper, go: () => goTo("inquiries"), hint: "Quote requests" },
              { label: "Messages", value: stats.messages, icon: Mail, go: () => goTo("contacts"), hint: "Contact form" },
            ].map((s) => (
              <button
                key={s.label}
                onClick={s.go}
                data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                className="group rounded-2xl border border-inkline bg-cream p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-spice hover:shadow-[0_10px_30px_-18px_rgba(0,0,0,0.4)]"
              >
                <div className="flex items-center justify-between">
                  <s.icon size={18} className="text-spice" />
                  <ArrowUpRight size={14} className="text-muted2 transition-colors group-hover:text-spice" />
                </div>
                <div className="mt-3 font-display text-2xl font-bold text-ink">{s.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">{s.label}</div>
                <div className="mt-1 text-[11px] text-muted2/80">{s.hint} →</div>
              </button>
            ))}
          </div>
        )}

        {stats && (
          <div className="mt-4 rounded-2xl border border-inkline bg-ink p-5 text-parchment" data-testid="admin-funnel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-saffron">Order funnel · all time · tap a number to open it</div>
              <button onClick={sendDigest} className="rounded-full border border-parchment/25 px-3 py-1 font-mono text-[10px] font-semibold text-parchment/80 transition-colors hover:border-saffron hover:text-saffron" data-testid="send-digest-btn" title="A summary of the last 24h is emailed to you automatically at 8pm ET">
                Email me today's digest now
              </button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:gap-6">
              {[
                { label: "Baskets started", value: stats.baskets, testid: "funnel-baskets", go: () => goTo("baskets") },
                { label: "Reached checkout", value: stats.orders, testid: "funnel-checkouts", go: () => goTo("orders", "all") },
                { label: "Paid", value: stats.paid_orders, testid: "funnel-paid", go: () => goTo("orders", "past") },
              ].map((f, i) => (
                <button key={f.label} onClick={f.go} className="group flex items-baseline gap-3 rounded-xl text-left transition-colors hover:text-saffron" data-testid={f.testid}>
                  <span className="font-display text-3xl font-bold underline-offset-4 group-hover:underline">{f.value}</span>
                  <span className="text-xs text-parchment/70">{f.label}{i === 2 && stats.orders ? ` · ${Math.round((stats.paid_orders / stats.orders) * 100)}% of checkouts` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={tabsRef} className="mt-8 flex flex-wrap gap-2 scroll-mt-28">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                tab === t.id ? "bg-ink text-parchment" : "bg-cream text-ink/70 hover:bg-ink/10"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "menu" ? (
            <MenuEditor />
          ) : tab === "baskets" ? (
            <BasketsPanel orders={orders} />
          ) : tab === "catering-menu" ? (
            <CateringEditor />
          ) : tab === "extras" ? (
            <ExtrasEditor />
          ) : tab === "testimonials" ? (
            <>
              <GoogleReviewSetting />
              <TestimonialsEditor />
            </>
          ) : tab === "subscribers" ? (
            <SubscribersPanel />
          ) : tab === "traffic" ? (
            <TrafficPanel />
          ) : tab === "site-photos" ? (
            <SitePhotosEditor />
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-inkline bg-cream">
              {tab === "orders" && (
                <div className="flex flex-wrap items-center gap-2 border-b border-inkline px-4 py-3" data-testid="order-filters">
                  {ORDER_FILTERS.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setOrderFilter(f.id)}
                      data-testid={`order-filter-${f.id}`}
                      className={`rounded-full px-3 py-1.5 font-mono text-[11px] font-semibold transition-colors ${orderFilter === f.id ? "bg-ink text-parchment" : "bg-ink/5 text-ink/70 hover:bg-ink/10"}`}
                    >
                      {f.label} · {orders.filter(f.test).length}
                    </button>
                  ))}
                  <span className="ml-auto text-xs text-muted2">{ORDER_FILTERS.find((f) => f.id === orderFilter)?.hint}</span>
                </div>
              )}
              {tab === "orders" && (
                <table className="w-full min-w-[760px] text-left text-sm" data-testid="orders-table">
                  <thead>
                    <tr className="border-b border-inkline font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">
                      <th className="p-4">Date</th><th className="p-4">Customer</th><th className="p-4">Meals</th>
                      <th className="p-4">Deliver to</th><th className="p-4">Total</th><th className="p-4">Status</th><th className="p-4">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted2">{orders.length === 0 ? "No orders yet." : "Nothing in this view."}</td></tr>}
                    {visibleOrders.map((o) => (
                      <tr key={o.id} className="border-b border-inkline/60 last:border-0" data-testid={`order-row-${o.id.slice(0, 8)}`}>
                        <td className="p-4 text-xs text-muted2">{new Date(o.created_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          <div className="font-semibold text-ink">{o.customer_name}</div>
                          <div className="text-xs text-muted2">{o.email}</div>
                        </td>
                        <td className="p-4 text-xs text-ink/80">
                          {o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                          {o.subscription && <span className="ml-1 rounded bg-spice/10 px-1.5 py-0.5 font-mono text-[10px] text-spice">SUB</span>}
                          {o.payment_status === "paid" ? (
                            <span className="ml-1 rounded bg-halal/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-halal">PAID</span>
                          ) : (
                            <span className="ml-1 rounded bg-saffron/15 px-1.5 py-0.5 font-mono text-[10px] text-muted2">UNPAID</span>
                          )}
                        </td>
                        <td className="p-4 text-xs text-muted2">{o.city}, {o.zip_code}</td>
                        <td className="p-4 font-mono text-xs font-bold">{fmt(o.total)}</td>
                        <td className="p-4">
                          <StatusSelect value={o.status} options={ORDER_STATUSES} onChange={(s) => setOrderStatus(o.id, s)} testid={`order-status-${o.id.slice(0, 8)}`} />
                        </td>
                        <td className="p-4">
                          {o.review_submitted ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-halal/10 px-2.5 py-1 font-mono text-[10px] font-bold text-halal" data-testid={`order-review-done-${o.id.slice(0, 8)}`}><Star size={10} /> Reviewed</span>
                          ) : o.review_request_sent ? (
                            <span className="rounded-full bg-saffron/15 px-2.5 py-1 font-mono text-[10px] font-semibold text-ink/70" data-testid={`order-review-sent-${o.id.slice(0, 8)}`}>Request sent</span>
                          ) : o.payment_status === "paid" ? (
                            <button
                              onClick={() => sendReviewRequest(o.id)}
                              data-testid={`order-review-send-${o.id.slice(0, 8)}`}
                              title={o.status === "Delivered" ? "Auto-sends 24h after delivery — or send it now" : "Send the review email now"}
                              className="rounded-full border border-inkline px-2.5 py-1 font-mono text-[10px] font-semibold text-ink transition-colors hover:border-spice hover:text-spice"
                            >
                              {o.status === "Delivered" ? "Scheduled · send now" : "Send request"}
                            </button>
                          ) : (
                            <span className="font-mono text-[10px] text-muted2">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === "inquiries" && (
                <table className="w-full min-w-[760px] text-left text-sm" data-testid="inquiries-table">
                  <thead>
                    <tr className="border-b border-inkline font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">
                      <th className="p-4">Received</th><th className="p-4">Contact</th><th className="p-4">Event</th>
                      <th className="p-4">Guests</th><th className="p-4">Estimate</th><th className="p-4">Photos</th><th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted2">No catering inquiries yet.</td></tr>}
                    {inquiries.map((q) => (
                      <tr key={q.id} className="border-b border-inkline/60 last:border-0" data-testid={`inquiry-row-${q.id.slice(0, 8)}`}>
                        <td className="p-4 text-xs text-muted2">{new Date(q.created_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          <div className="font-semibold text-ink">{q.name}</div>
                          <div className="text-xs text-muted2">{q.email} · {q.phone}</div>
                        </td>
                        <td className="p-4 text-xs text-ink/80">{q.event_type} · {q.event_date} · {q.location}</td>
                        <td className="p-4 font-mono text-xs">{q.guests}</td>
                        <td className="p-4 font-mono text-xs font-bold">{q.estimate ? fmt(q.estimate) : "—"}</td>
                        <td className="p-4">
                          {q.attachments?.length ? (
                            <div className="flex -space-x-2" data-testid={`inquiry-attachments-${q.id.slice(0, 8)}`}>
                              {q.attachments.map((a, i) => (
                                <a key={a} href={imgSrc(a)} target="_blank" rel="noreferrer" title={`Reference photo ${i + 1}`} data-testid={`inquiry-attachment-${q.id.slice(0, 8)}-${i}`}>
                                  <img src={imgSrc(a)} alt={`Reference ${i + 1}`} className="h-10 w-10 rounded-lg border-2 border-parchment object-cover transition-transform hover:z-10 hover:scale-125" />
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted2">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          <StatusSelect value={q.status} options={INQUIRY_STATUSES} onChange={(s) => setInquiryStatus(q.id, s)} testid={`inquiry-status-${q.id.slice(0, 8)}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === "contacts" && (
                <table className="w-full min-w-[700px] text-left text-sm" data-testid="contacts-table">
                  <thead>
                    <tr className="border-b border-inkline font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">
                      <th className="p-4">Date</th><th className="p-4">From</th><th className="p-4">Subject</th><th className="p-4">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted2">No messages yet.</td></tr>}
                    {contacts.map((m) => (
                      <tr key={m.id} className="border-b border-inkline/60 last:border-0" data-testid={`contact-row-${m.id.slice(0, 8)}`}>
                        <td className="p-4 text-xs text-muted2">{new Date(m.created_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          <div className="font-semibold text-ink">{m.name}</div>
                          <div className="text-xs text-muted2">{m.email}</div>
                        </td>
                        <td className="p-4 text-xs font-semibold text-spice">{m.subject}</td>
                        <td className="max-w-sm p-4 text-xs text-ink/80">{m.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
